import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useGroups, useStakeholders, useUpdateGroupDetails, useSendAppDownload, useGroupMessageLog } from "../lib/api";
import { resolveJourney, nb, STATUS_META, SERVICE_TYPES } from "../lib/journey";
import { useTenantTerms } from "../lib/TenantConfigContext";
import { joinStatusFromGroup, sendersFromMessageLog } from "../lib/members";
import { useAuth } from "../lib/AuthContext";
import ComplaintsPanel from "../components/complaints/ComplaintsPanel";
import { StepNode, statusChipCls } from "../components/journey/StepControls";
import { PickupDropChoice } from "../components/journey/PickupDropChoice";
import { SentimentHistoryPanel, StaffToneReviewPanel } from "../components/journey/InsightPanels";
import { ArrowLeft, Loader2, User, MapPin, Wrench, Phone, Pencil, X, Download } from "lucide-react";

const InfoCell = ({ label, value, side, icon: Icon }) => (
  <div>
    <div className="uplabel mb-1">{label}</div>
    <div className="flex items-center gap-1.5 text-sm text-slate-800">
      {Icon && <Icon size={14} className="shrink-0 text-slate-400" />}
      {nb(value) ? value : <span className="text-slate-400">Not yet</span>}
    </div>
    {nb(side) && (
      <div className="flex items-center gap-1.5 mt-0.5 text-sm text-slate-500">
        <Phone size={12} className="shrink-0" />
        <span>{side}</span>
      </div>
    )}
  </div>
);

// Maps a member's raw role tag (unchanged since it's also what join-status
// matching keys off — see joinStatusFromGroup — matching text the webhook
// writes in the platform's own vocabulary, never the brand's) to this
// brand's terminology for display only. "Customer"/"Alt Customer" have no
// entry and render as-is: a customer isn't a stakeholder role a brand renames.
const STAFF_ROLE_KEY_BY_TAG = {
  "Service Advisor": "sa",
  GM: "gm",
  WM: "wm",
  CCM: "ccm",
  PSF: "psf",
  Receptionist: "receptionist",
};

function displayRoleTag(role, roleShort) {
  const key = STAFF_ROLE_KEY_BY_TAG[role];
  return key ? roleShort(key) : role;
}

function MembersPanel({ g }) {
  const workshopLocation = g["Workshop Location"];
  const { data: stakeholdersData, isLoading: loading, error: stakeholdersError } = useStakeholders(workshopLocation);
  const { roleShort } = useTenantTerms();
  const stakeholders = stakeholdersData?.stakeholders || [];
  const error = stakeholdersError?.message || null;
  // Same evidence the desk panel uses: anyone who has sent a message in this
  // group is in it, whatever members_* does or does not say.
  const { data: messageLog } = useGroupMessageLog(g?._row);
  const senderNumbers = sendersFromMessageLog(messageLog?.rows);

  const primaryMembers = [
    {
      role: "Customer",
      name: g["Customer Name"],
      number: g["Customer Number"],
      join_status: joinStatusFromGroup(g, "Customer", g["Customer Name"], g["Customer Number"], senderNumbers),
    },
    ...(nb(g["Alternate Customer Number"])
      ? [{
          role: "Alt Customer",
          name: `${g["Customer Name"] || "Customer"} (Alt)`,
          number: g["Alternate Customer Number"],
          join_status: joinStatusFromGroup(g, "Alt Customer", g["Customer Name"], g["Alternate Customer Number"], senderNumbers),
        }]
      : []),
    {
      role: "Service Advisor",
      name: g["Service Advisor Name"],
      number: g["Service Advisor Number"],
      join_status: joinStatusFromGroup(g, "Service Advisor", g["Service Advisor Name"], g["Service Advisor Number"], senderNumbers),
    },
  ].filter((m) => nb(m.name) || nb(m.number));

  const allMembers = [
    ...primaryMembers,
    ...stakeholders.map((s) => ({
      ...s,
      join_status: joinStatusFromGroup(g, s.role, s.name, s.number, senderNumbers),
    })),
  ];

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="members-panel">
      <h3 className="font-work text-base font-medium text-slate-800 mb-4">Members</h3>
      
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="animate-spin" size={16} /> Loading members…
        </div>
      ) : error ? (
        <p className="text-xs text-red-600">{error}</p>
      ) : allMembers.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="members-not-checked">No members found</p>
      ) : (
        <div className="space-y-3" data-testid="members-list">
          {allMembers.map((s, i) => (
            <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0 last:pb-0">
              <div className="space-y-0.5">
                <div className="flex items-center gap-1.5">
                  <span className="text-slate-800 font-medium">{s.name || "N/A"}</span>
                  {s.role && (
                    <span className="text-[10px] font-medium bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                      {displayRoleTag(s.role, roleShort)}
                    </span>
                  )}
                </div>
                {nb(s.number) && (
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    <Phone size={12} className="shrink-0 text-slate-400" />
                    <span>{s.number}</span>
                  </div>
                )}
              </div>
              <div>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${statusChipCls(s.join_status)}`}>
                  {s.join_status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const editInputCls =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500";

function EditGroupModal({ g, row, onClose }) {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const update = useUpdateGroupDetails();
  const isServiceAdvisor = me?.role === "sa";
  
  const [f, setF] = useState({
    customer_name: g["Customer Name"] || "",
    customer_number: (g["Customer Number"] || "").replace(/^\+?91/, "").replace(/\D/g, ""),
    service_advisor_name: g["Service Advisor Name"] || "",
    service_advisor_number: (g["Service Advisor Number"] || "").replace(/^\+?91/, "").replace(/\D/g, ""),
    service_type: g["Service Type"] || "",
    pickup_drop: g["Pick & Drop"] || "",
  });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const setPhoneDigits = (k) => (e) => {
    const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 10);
    setF((s) => ({ ...s, [k]: digits }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        row,
        customer_name: f.customer_name,
        customer_number: `+91${f.customer_number}`,
        service_type: f.service_type,
        ...(f.pickup_drop ? { pickup_drop: f.pickup_drop } : {}),
      };
      
      if (!isServiceAdvisor) {
        payload.service_advisor_name = f.service_advisor_name;
        payload.service_advisor_number = f.service_advisor_number ? `+91${f.service_advisor_number}` : "";
      }
      
      await update.mutateAsync(payload);
      toast.success("Group details updated. Invite resent to any changed numbers.");
      await queryClient.invalidateQueries({ queryKey: ["groups"] });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-lg p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-xl bg-white p-6 space-y-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-group-modal"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-work text-lg font-semibold text-slate-900">Edit Group Details</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="uplabel mb-1.5 block">Customer Name</label>
            <input data-testid="edit-input-customer-name" className={editInputCls} value={f.customer_name} onChange={set("customer_name")} />
          </div>
          <div>
            <label className="uplabel mb-1.5 block">Customer Number</label>
            <div className="flex items-stretch rounded-md border border-slate-300 bg-white focus-within:ring-2 focus-within:ring-blue-500/40 focus-within:border-blue-500">
              <span className="flex items-center px-3 text-sm text-slate-500 border-r border-slate-300 select-none">+91</span>
              <input
                data-testid="edit-input-customer-number"
                className="w-full rounded-r-md px-3 py-2 text-sm focus:outline-none"
                placeholder="10-digit number"
                inputMode="numeric"
                value={f.customer_number}
                onChange={setPhoneDigits("customer_number")}
              />
            </div>
          </div>
          {!isServiceAdvisor && (
            <>
              <div>
                <label className="uplabel mb-1.5 block">Service Advisor Name</label>
                <input data-testid="edit-input-sa-name" className={editInputCls} value={f.service_advisor_name} onChange={set("service_advisor_name")} />
              </div>
              <div>
                <label className="uplabel mb-1.5 block">Service Advisor Number</label>
                <div className="flex items-stretch rounded-md border border-slate-300 bg-white focus-within:ring-2 focus-within:ring-blue-500/40 focus-within:border-blue-500">
                  <span className="flex items-center px-3 text-sm text-slate-500 border-r border-slate-300 select-none">+91</span>
                  <input
                    data-testid="edit-input-sa-number"
                    className="w-full rounded-r-md px-3 py-2 text-sm focus:outline-none"
                    placeholder="10-digit number"
                    inputMode="numeric"
                    value={f.service_advisor_number}
                    onChange={setPhoneDigits("service_advisor_number")}
                  />
                </div>
              </div>
            </>
          )}
          <div>
            <label className="uplabel mb-1.5 block">Service Type</label>
            <select data-testid="edit-select-service-type" className={editInputCls} value={f.service_type} onChange={set("service_type")}>
              <option value="">Select service type…</option>
              {SERVICE_TYPES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="uplabel mb-1.5 block">Pick &amp; Drop</label>
            <PickupDropChoice testid="edit-pickup-drop" value={f.pickup_drop} onChange={(v) => setF((s) => ({ ...s, pickup_drop: v }))} />
          </div>
          <p className="text-xs text-slate-500">
            Changing a phone number automatically resends the WhatsApp group invite to the new number.
          </p>
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-md border border-slate-300 text-slate-700 font-medium py-2 text-sm hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="edit-save-btn"
              disabled={update.isPending}
              className="flex-1 flex items-center justify-center gap-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 text-sm transition-colors disabled:opacity-60"
            >
              {update.isPending ? <><Loader2 className="animate-spin" size={14} /> Saving…</> : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AlternateCustomerModal({ g, row, onClose }) {
  const queryClient = useQueryClient();
  const update = useUpdateGroupDetails();
  const [digits, setDigits] = useState(
    (g["Alternate Customer Number"] || "").replace(/^\+?91/, "").replace(/\D/g, "")
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!digits || digits.length !== 10) {
      toast.error("Enter a valid 10-digit mobile number.");
      return;
    }
    try {
      await update.mutateAsync({ row, alternate_customer_number: `+91${digits}` });
      toast.success("Alternate customer number saved.");
      await queryClient.invalidateQueries({ queryKey: ["groups"] });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-lg p-4" onClick={onClose}>
      <div
        className="w-full max-w-sm rounded-xl bg-white p-6 space-y-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="alternate-customer-modal"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-work text-lg font-semibold text-slate-900">Alternate customer number</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="uplabel mb-1.5 block">Mobile number</label>
            <div className="flex items-stretch rounded-md border border-slate-300 bg-white focus-within:ring-2 focus-within:ring-blue-500/40 focus-within:border-blue-500">
              <span className="flex items-center px-3 text-sm text-slate-500 border-r border-slate-300 select-none">+91</span>
              <input
                data-testid="alternate-customer-number-input"
                className="w-full rounded-r-md px-3 py-2 text-sm focus:outline-none"
                placeholder="10-digit number"
                inputMode="numeric"
                value={digits}
                onChange={(e) => setDigits((e.target.value || "").replace(/\D/g, "").slice(0, 10))}
              />
            </div>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-md border border-slate-300 text-slate-700 font-medium py-2 text-sm hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="alternate-customer-save-btn"
              disabled={update.isPending}
              className="flex-1 flex items-center justify-center gap-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 text-sm transition-colors disabled:opacity-60"
            >
              {update.isPending ? <><Loader2 className="animate-spin" size={14} /> Saving…</> : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function JourneyDetail() {
  const { row } = useParams();
  const navigate = useNavigate();
  
  const { data, isLoading, isError, error } = useGroups();
  
  const [editOpen, setEditOpen] = useState(false);
  const [alternateOpen, setAlternateOpen] = useState(false);
  const sendAppDownload = useSendAppDownload();
  const { stageOverlay, roleLabel, featureFlags, unitLabel } = useTenantTerms();

  const handleSendAppDownload = async () => {
    try {
      await sendAppDownload.mutateAsync({ row: Number(row) });
      toast.success("App download link sent to the WhatsApp group.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send app download link.");
    }
  };

  if (isLoading)
    return (
      <div className="flex items-center gap-2 text-slate-500">
        <Loader2 className="animate-spin" size={18} /> Loading…
      </div>
    );
  if (isError)
    return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 text-sm">Failed to load: {error?.response?.data?.detail || error.message}</div>;

  const g = (data?.rows || []).find((r) => String(r._row) === String(row));
  if (!g)
    return (
      <div className="space-y-4">
        <p className="text-slate-500">Group not found.</p>
        <Link to="/journey" className="text-blue-600 text-sm">← Back to list</Link>
      </div>
    );

  const steps = resolveJourney(g, stageOverlay, featureFlags);
  const statusMeta = STATUS_META[g["Status"]] || { label: g["Status"] || "—", cls: "bg-slate-100 text-slate-600 border-slate-200" };

  return (
    <div className="space-y-6" data-testid="journey-detail-page">
      <button onClick={() => navigate(-1)} data-testid="back-btn" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-work text-2xl font-semibold tracking-tight text-slate-900">{g["Group Name"] || "Untitled group"}</h1>
            <div className="text-sm text-slate-500 mt-1">{nb(g["Description"]) ? g["Description"] : "No service type"}</div>
          </div>
          <div className="flex items-center gap-2">
            {nb(g["Feedback Notified"]) && (
              <span
                data-testid="job-card-closed-badge"
                className="text-xs font-semibold px-3 py-1.5 rounded-full border bg-violet-50 text-violet-700 border-violet-200"
              >
                {unitLabel} Closed
              </span>
            )}
            {nb(g["WA Deleted At"]) && (
              <span
                data-testid="wa-deleted-badge"
                title={`WhatsApp group was deleted on ${g["WA Deleted At"]}`}
                className="text-xs font-semibold px-3 py-1.5 rounded-full border bg-slate-100 text-slate-600 border-slate-300"
              >
                WhatsApp group deleted
              </span>
            )}
            <span data-testid="status-badge" className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${statusMeta.cls}`}>
              {statusMeta.label}
            </span>
            <button
              onClick={handleSendAppDownload}
              disabled={sendAppDownload.isPending || nb(g["WA Deleted At"])}
              title={nb(g["WA Deleted At"]) ? "WhatsApp group was deleted — read-only" : undefined}
              data-testid="send-app-download-btn"
              className="flex items-center gap-1.5 text-xs font-semibold text-white bg-emerald-600 rounded-full px-3.5 py-1.5 shadow-sm shadow-emerald-600/20 transition-colors hover:bg-emerald-700 disabled:opacity-60"
            >
              {sendAppDownload.isPending ? <Loader2 className="animate-spin" size={13} /> : <Download size={13} />}
              Download App
            </button>
            <button
              onClick={() => setEditOpen(true)}
              data-testid="edit-group-btn"
              className="flex items-center gap-1.5 text-xs font-medium text-slate-600 border border-slate-300 rounded-full px-3 py-1.5 hover:bg-slate-50"
            >
              <Pencil size={13} /> Edit
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5 pt-5 border-t border-slate-100">
          <InfoCell label="Customer" icon={User} value={g["Customer Name"]} side={g["Customer Number"]} />
          <InfoCell label="Workshop" icon={MapPin} value={g["Workshop Location"]} />
          <InfoCell label={roleLabel("sa")} icon={Wrench} value={g["Service Advisor Name"]} side={g["Service Advisor Number"]} />
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-slate-100 text-sm text-slate-600">
          <span>Customer joined with alternate number</span>
          {nb(g["Alternate Customer Number"]) && (
            <span className="flex items-center gap-1 text-slate-800">
              <Phone size={12} className="text-slate-400 shrink-0" />
              {g["Alternate Customer Number"]}
            </span>
          )}
          <button
            type="button"
            onClick={() => setAlternateOpen(true)}
            data-testid="alternate-customer-edit-btn"
            className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
          >
            {nb(g["Alternate Customer Number"]) ? "Edit" : "Add number"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SentimentHistoryPanel row={row} />
        <MembersPanel g={g} />
      </div>

      <ComplaintsPanel row={row} />

      <StaffToneReviewPanel row={row} />

      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4" data-testid="stepper">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-work text-base font-medium text-slate-800">Service Journey</h3>
          <Link
            to={`/chat-history?group=${encodeURIComponent(g["Group Name"] || "")}`}
            data-testid="view-chat-history-link"
            className="text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            View chat history ↗
          </Link>
        </div>
        {steps.map((step, idx) => (
          <StepNode key={step.key} step={step} isLast={idx === steps.length - 1} index={idx} g={g} />
        ))}
      </div>

      {editOpen && <EditGroupModal g={g} row={row} onClose={() => setEditOpen(false)} />}
      {alternateOpen && <AlternateCustomerModal g={g} row={row} onClose={() => setAlternateOpen(false)} />}
    </div>
  );
}